/**
 * drawer-state — Riflette sul <body> lo stato "almeno un drawer è aperto".
 *
 * Marca <body data-ik-drawer="open"> quando uno qualsiasi dei drawer del
 * frontend (carrello, login, registrazione, checkout, ordine, termini) è
 * visibile, e lo rimuove quando sono tutti chiusi. È un fatto NEUTRO: il
 * frontend non sa chi reagirà a questo stato. Il CSS del frontend lo usa
 * per togliere di mezzo widget di terze parti che si sovrappongono ai drawer
 * (es. la chat in basso a destra iniettata via GTM), senza che il JS conosca
 * quel widget.
 *
 * Lo stato è derivato dai flag reattivi degli store Alpine: un Alpine.effect()
 * ricalcola automaticamente a ogni cambio, così non tocchiamo i singoli
 * metodi open/close (che sono molti).
 */

/**
 * True se almeno un drawer del frontend è aperto.
 * Va chiamata DENTRO un Alpine.effect() perché sia l'effect a ri-eseguire
 * quando uno di questi flag reattivi cambia.
 *
 * TODO(tu): completa l'espressione booleana leggendo i flag degli store.
 *
 * Gli store e i loro flag rilevanti (vedi stores/*.js):
 *   cart     → drawerOpen
 *   auth     → drawerOpen, registerDrawerOpen, registerTermsOpen, registerPrivacyOpen
 *   checkout → drawerOpen, summaryOpen, orderOpen, termsOpen, shifting
 *
 * Nota: `shifting` di checkout diventa true ~immediatamente all'avvio del
 * flusso (prima ancora che il drawer form sia montato), quindi includerlo
 * fa sparire la chat senza flicker durante l'animazione di apertura.
 *
 * Usa optional chaining (cart?.drawerOpen): gli store esistono già dopo
 * Alpine.start(), ma l'optional chaining rende la funzione difensiva.
 *
 * @param {import('alpinejs').Alpine} Alpine
 * @returns {boolean}
 */
function isAnyDrawerOpen(Alpine) {
    const cart        = Alpine.store('cart');
    const auth        = Alpine.store('auth');
    const checkout    = Alpine.store('checkout');
    const waitingList = Alpine.store('waitingList');

    return Boolean(
        cart?.drawerOpen ||
        auth?.drawerOpen ||
        auth?.registerDrawerOpen ||
        auth?.registerTermsOpen ||
        auth?.registerPrivacyOpen ||
        checkout?.drawerOpen ||
        checkout?.summaryOpen ||
        checkout?.orderOpen ||
        checkout?.termsOpen ||
        checkout?.shifting ||
        waitingList?.drawerOpen
    );
}

/**
 * Registra l'osservatore reattivo. Da chiamare DOPO Alpine.start(),
 * quando gli store sono già istanziati.
 *
 * @param {import('alpinejs').Alpine} Alpine
 */
export function initDrawerState(Alpine) {
    let last = null; // evita scritture DOM ridondanti

    Alpine.effect(() => {
        const open = isAnyDrawerOpen(Alpine);
        if (open === last) return;
        last = open;

        if (open) {
            document.body.setAttribute('data-ik-drawer', 'open');
        } else {
            document.body.removeAttribute('data-ik-drawer');
        }
    });
}
